import { AiPill } from '@/components/AiPill';
import { MoneyText } from '@/components/MoneyText';
import { StatusChip } from '@/components/StatusChip';
import { useEffect, useEffectEvent, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  type Theme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { TabularText } from '@/components/TabularText';
import { useTranslation } from 'react-i18next';
import {
  aiRefusal,
  ErrorCodes,
  isAiWalletRefusal,
  isApiError,
  openingDiffers,
  type AiRefusal,
  type AiWalletReason,
} from '@/api/errors';
import { AiWalletPrompt } from '@/features/aiWallet/AiWalletPrompt';
import { backendFields } from '@/api/errorDetails';
import { importPresetsApi, type ImportPreset } from '@/api/importPresets';
import { importProfilesApi, type ImportProfile } from '@/api/importProfiles';
import {
  importsApi,
  type ImportBatch,
  type ImportFit,
  type ImportPreview,
  type ImportPreviewBody,
  type ImportProposedMapping,
  type ImportUpload,
} from '@/api/imports';
import { CurrentSettingsLine } from '@/features/automation/CurrentSettingsLine';
import { useAutomationGate } from '@/features/automation/useAutomationGate';
import { aiDisabledCopy, errorCopy } from '@/api/errorCopy';
import { SubmitButton } from '@/components/SubmitButton';
import { useConfirm } from '@/components/useConfirm';
import type { TopUpSwap } from '@/features/aiWallet/useTopUpSwap';
import { DISCARD_TITLE_ID, DiscardPanel } from './DiscardPanel';
import { FitReasonsBanner } from './FitStep';
import { BEFORE_OPENING_CODES, fitView, type FitView } from './fitReasons';
import { formatFileType, formatInstitution, sameName, uniqueName } from './formatName';
import { RawSample } from './RawSample';
import { RowNumberField } from './RowNumberField';
import { useImportOpening } from './useImportOpening';
import { formatCivilDate, formatCivilRangeNear, formatNumber, formatPercent } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { EarlierHistoryOpening } from './EarlierHistoryOpening';
import { needsDifferenceAccepted, type StaleOpeningDifference } from './openingCheck';
import { generateRequestId } from '@/utils/requestId';
import { resolveTokens } from '@/theme/theme';
import { useUnsavedGuard } from '@/shared/useUnsavedGuard';
import { UnsavedChangesDialog } from '@/components/UnsavedChangesDialog';
import { useErrorFocus } from '@/shared/useErrorFocus';
import { useDirty } from '@/shared/useDirty';
import { DATE_FORMATS, firstSampleDateCell, parseDateCell } from './dateFormats';
import { ImportActivityBar, ImportProgress } from './ImportProgress';
import {
  CANONICAL_FIELDS,
  OPTIONAL_FIELDS,
  formFromProfile,
  formFromProposal,
  type CanonicalField,
  type MappingForm,
} from './mapping';

const NONE = '';
const SAVE_NEW = 'new';
/** A mapping in use that is not a saved format or a preset. Not `''`: MUI paints that as blank. */
const UNSAVED = 'unsaved';
const NAMED_FIELDS = new Set(['headerRow', 'firstDataRow']);
/** Configuration's Advanced section (TMMAPP-0140 item 1): two column pickers, then the file's shape. */
const ADVANCED_COLUMNS = ['memo', 'externalId'] as const satisfies readonly CanonicalField[];
const ADVANCED_FIELDS: ReadonlySet<string> = new Set([
  ...ADVANCED_COLUMNS,
  'delimiter',
  'encoding',
]);
const MAIN_COLUMNS = CANONICAL_FIELDS.filter((field) => !ADVANCED_FIELDS.has(field));

type PreviewOutcome = 'advance' | 'poor' | 'stay';

/** `IMPORT_MAPPING_INVALID` names fields as a string list or a string map. */
function namedInvalidFields(error: unknown): string[] {
  if (!isApiError(error)) return [];
  const backend = error.details.backend;
  if (typeof backend === 'object' && backend !== null && Array.isArray(backend.fields)) {
    return backend.fields.filter(
      (item): item is string => typeof item === 'string' && item.length > 0,
    );
  }
  return Object.keys(backendFields(error));
}

function coveredDefault(next: ImportPreview): string {
  return next.plannedCovered.coveredThrough ?? '';
}

/**
 * Confirm's line preview (TMMAPP-0139 item 3): its own box, about eight rows tall (a max height on
 * the theme's spacing), scrolling both ways inside with the header row sticky, so a long file never
 * makes the dialog long. At `md` and up the import body lays Confirm out as a column that fills it
 * (TMMAPP-0140 item 5): when the step is taller than the body, a window of more than five lines gives
 * up height, down to about five rows (`theme.spacing(26)`), before the body scrolls.
 */
const PREVIEW_MIN_ROWS = 5;

/** `minHeight`: the five-row floor, `theme.spacing(26)` (a breakpoint value can't be a callback). */
function previewWindowSx(shrinks: boolean, minHeight: string) {
  return {
    overflow: 'auto',
    maxHeight: (theme: Theme) => theme.spacing(44),
    ...(shrinks ? { minHeight: { md: minHeight }, flex: { md: '0 1 auto' } } : {}),
    minWidth: 0,
    width: '100%',
    '& thead th': { position: 'sticky', top: 0, zIndex: 1 },
  } as const;
}

/** The window's holder (it carries the refresh overlay): a column that may shrink with it. */
function previewShellSx(shrinks: boolean, minHeight: string) {
  return {
    position: 'relative',
    flexShrink: 0,
    ...(shrinks
      ? {
          display: { md: 'flex' },
          flexDirection: 'column',
          flex: { md: '0 1 auto' },
          minHeight: { md: minHeight },
        }
      : {}),
  } as const;
}

/**
 * A payee wraps to at most two lines, then an ellipsis; the full text is in a tooltip. The column
 * never narrows below `PAYEE_MIN_CH` (below `sm` the table scrolls sideways in its box instead), so
 * two lines always hold more than `PAYEE_TOOLTIP_CHARS` characters, even upper-case ones: a payee
 * that short is never cut off, and a longer one gets the tooltip and a tab stop.
 */
const PAYEE_MIN_CH = 24;
const PAYEE_TOOLTIP_CHARS = 32;

function PayeeCell({ text }: { text: string }) {
  const tokens = resolveTokens(useTheme());
  const long = text.length > PAYEE_TOOLTIP_CHARS;
  const clamp = (
    <Box
      component="span"
      data-payee-clamp=""
      {...(long ? { tabIndex: 0 } : {})}
      sx={{
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        minInlineSize: `${PAYEE_MIN_CH}ch`,
        overflowWrap: 'break-word',
        '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
      }}
    >
      {text}
    </Box>
  );
  return long ? (
    <Tooltip title={text} describeChild>
      {clamp}
    </Tooltip>
  ) : (
    clamp
  );
}

interface ConfirmStepProps {
  accountId: number;
  accountName: string;
  currency: string | null;
  upload: ImportUpload;
  /** `firstLineDate`: the earliest parsed line (the preview's summary), for the register link. */
  onCommitted: (batchId: number, historical: number | null, firstLineDate: string | null) => void;
  onDiscarded: () => void;
  /** Closes the import modal: an AI prompt's subscribe card closes it first (DLG-005). */
  onCloseHost?: (() => void) | undefined;
  onBatchState: (batch: ImportBatch) => void;
  /** The modal footer's right-hand slot: the commit button renders there (null while it mounts). */
  actionsHost?: HTMLElement | null;
  /** Reports a commit in flight (or done), so the modal's Back stays out of its way. */
  onBusyChange?: (busy: boolean) => void;
  /** The modal's panel for the discard confirmation (`undefined` outside the modal; DLG-005). */
  panelHost?: HTMLElement | null;
  /**
   * Reports the discard confirmation (or the AI prompt's top-up, TMMAPP-0118 C11) showing, so the
   * modal hides its own content.
   */
  onConfirming?: (confirming: boolean) => void;
  /** The AI prompt's top-up registers its close request with the modal (TMMAPP-0119 item 16). */
  onTopUpCloseRequest?: (request: (() => void) | null) => void;
  /** Configuration is the mapping; Confirm is what will be imported. The same instance stays mounted. */
  phase: 'configuration' | 'confirm';
  /** A successful preview (or a POOR one from Next) moves to Confirm. */
  onAdvance: () => void;
  /** The account's served institution; the format name uses the account name when this is blank. */
  institution: string | null;
  /** The uploaded file's name, for the format-name default. Empty on a resume that omits it. */
  sourceFilename: string;
  /** AI is on for this person (`GET /me`). Suggest mapping stays hidden when it is off. */
  aiEnabled: boolean;
  /**
   * A resumed Confirm has no preview on the batch: run one preview when this screen opens.
   * An upload that already carried a preview does not.
   */
  autoPreview?: boolean;
  /**
   * The uploader owns the account: import mapping AI is owner-only (TMMAPI-0152, MM-D-133). A
   * grantee gets profile, preset or deterministic mapping only — no "Suggest mapping". Required, so
   * a host can't show it by omission.
   */
  aiMapping: boolean;
  /** The account's opening is pending (TMMAPP-0123 item 17): the starting balance is asked here. */
  openingPending?: boolean;
  /** The account's opening date, for the all-before-opening notice (TMMAPP-0139 item 8). */
  openingOn?: string | null;
  /** Opens the account's starting balance editor; absent, the notice offers no link. */
  onChangeOpening?: (() => void) | undefined;
}

function confidenceLabel(confidence: number | null, locale: string): string {
  if (confidence === null) return '';
  const fraction = confidence <= 1 ? confidence : confidence / 100;
  return formatPercent(fraction, locale);
}

/**
 * The prototype's review stat box (P:1790): 1px `line`, `overline` label, `stat` value. At `md` and
 * up the six share one row (TMMAPP-0140 item 5): tighter padding, the `statSm` value, each box as
 * wide as its content allows, a long value (the dates) wrapping to a second line.
 */
function StatBox({
  label,
  variant,
  children,
}: {
  label: string;
  variant: 'stat' | 'statSm';
  children: ReactNode;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      data-stat-box=""
      sx={{
        p: { xs: 1.5, md: 1 },
        paddingInline: { md: 1.5 },
        minWidth: { xs: 120, md: 'auto' },
        flex: { md: '1 1 auto' },
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
      }}
    >
      <Typography variant="overline" component="div">
        {label}
      </Typography>
      <Typography variant={variant} component="div">
        {children}
      </Typography>
    </Box>
  );
}

export function ConfirmStep({
  accountId,
  accountName,
  currency,
  upload,
  onCommitted,
  onDiscarded,
  onCloseHost,
  onBatchState,
  actionsHost,
  onBusyChange,
  panelHost,
  onConfirming,
  onTopUpCloseRequest,
  phase,
  onAdvance,
  institution,
  sourceFilename,
  aiEnabled,
  autoPreview = false,
  aiMapping,
  openingPending = false,
  openingOn = null,
  onChangeOpening,
}: ConfirmStepProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const gate = useAutomationGate();
  const beforeOpeningNoticeId = useId();
  const tilesId = useId();
  const advancedId = useId();
  const plannedCoveredId = useId();
  // The tiles sit in one compact row at `md` and up (TMMAPP-0140 item 5): the theme's smaller stat.
  const statVariant = useMediaQuery(theme.breakpoints.up('md')) ? 'statSm' : 'stat';
  const discarding = useConfirm<true>();
  const topUpSwap = useMemo<TopUpSwap | undefined>(
    () =>
      panelHost !== undefined && onConfirming !== undefined && onTopUpCloseRequest !== undefined
        ? {
            panelHost,
            onSwap: onConfirming,
            titleId: DISCARD_TITLE_ID,
            onCloseRequest: onTopUpCloseRequest,
          }
        : undefined,
    [panelHost, onConfirming, onTopUpCloseRequest],
  );
  const [proposal, setProposal] = useState<ImportProposedMapping>(upload.proposedMapping);
  const [form, setForm] = useState<MappingForm>(() =>
    formFromProposal(
      upload.proposedMapping,
      upload.detected.delimiter,
      upload.detected.encoding,
      upload.suggestedProfileId,
    ),
  );
  const [profiles, setProfiles] = useState<ImportProfile[]>([]);
  const [presets, setPresets] = useState<ImportPreset[]>([]);
  const [formatsReady, setFormatsReady] = useState(false);
  const [formatsKey, setFormatsKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(upload.preview);
  const [ownPreview, setOwnPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState(false);
  const [suggestError, setSuggestError] = useState<string | null>(null);
  const [suggestBlocked, setSuggestBlocked] = useState(false);
  const [noPrice, setNoPrice] = useState(false);
  const [suggestRefusal, setSuggestRefusal] = useState<
    (AiRefusal & { reason: AiWalletReason }) | null
  >(null);
  // Save is on by default only when the person has no saved format and no preset (TMMAPP-0131).
  // With any format, "Save new format…" turns it on. Scope starts at All accounts.
  const [saveProfile, setSaveProfile] = useState(false);
  const [saveNew, setSaveNew] = useState(false);
  const [typedName, setTypedName] = useState<string | null>(null);
  const [profileScope, setProfileScope] = useState<'ACCOUNT' | 'OWNER'>('OWNER');
  const [presetId, setPresetId] = useState<number | null>(() =>
    upload.proposedMapping.source === 'PRESET' ? upload.suggestedPresetId : null,
  );
  const [nameError, setNameError] = useState<string | null>(null);
  const [fxUnavailable, setFxUnavailable] = useState(false);
  const [coveredThrough, setCoveredThrough] = useState(() =>
    upload.preview === null ? '' : coveredDefault(upload.preview),
  );
  const [uncovered, setUncovered] = useState<'WIPE' | 'KEEP'>('WIPE');
  // Earlier history (TMMAPI-0191) is imported by default; unticking it sends `includeBeforeOpening:
  // false`. A DIFFERS opening imports only once "Import anyway, I'll reconcile" is ticked.
  const [includeEarlier, setIncludeEarlier] = useState(true);
  const [acceptDifference, setAcceptDifference] = useState(false);
  const [staleDifference, setStaleDifference] = useState<StaleOpeningDifference | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const [advancedSignal, setAdvancedSignal] = useState('');
  const [committing, setCommitting] = useState(false);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const [batchStatus, setBatchStatus] = useState<ImportBatch['status'] | null>(null);
  const seq = useRef(0);
  const alive = useRef(true);
  const seededSave = useRef(false);
  const autoStarted = useRef(false);
  const onBatchStateRef = useRef(onBatchState);
  const titleRef = useRef<HTMLHeadingElement>(null);
  // A header or column edit wins over the suggested profile that arrives with the format list.
  const [mappingEdited, setMappingEdited] = useState(false);
  const [firstDataSet, setFirstDataSet] = useState(false);
  const [appliedSuggestedId, setAppliedSuggestedId] = useState<number | null>(null);
  const preambleRows = upload.detected.preambleRows;
  const sampleMax = preambleRows + Math.max(upload.detected.sampleRows.length, 1);
  const suggestedProfile =
    upload.suggestedProfileId !== null
      ? profiles.find((p) => p.id === upload.suggestedProfileId)
      : undefined;
  if (
    phase === 'configuration' &&
    presetId === null &&
    !mappingEdited &&
    suggestedProfile !== undefined &&
    appliedSuggestedId !== suggestedProfile.id
  ) {
    setAppliedSuggestedId(suggestedProfile.id);
    setForm(formFromProfile(suggestedProfile));
  }
  const { dirty } = useDirty(
    { form, saveProfile, typedName, profileScope, uncovered },
    upload.suggestedProfileId === null || appliedSuggestedId === upload.suggestedProfileId,
  );
  const guard = useUnsavedGuard(dirty || committing, { busy: discarding.busy });
  const busy = committing || importedCount !== null;
  const opening = useImportOpening({ pending: openingPending, preview, currency, disabled: busy });
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);
  const { rootRef, focusErrors } = useErrorFocus<HTMLDivElement>();

  useEffect(() => {
    onBatchStateRef.current = onBatchState;
  }, [onBatchState]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      seq.current += 1;
    };
  }, []);

  useEffect(() => {
    if (phase !== 'configuration') return;
    let cancelled = false;
    void Promise.all([
      importProfilesApi.list().catch(() => [] as ImportProfile[]),
      importPresetsApi.list().catch(() => [] as ImportPreset[]),
    ]).then(([nextProfiles, nextPresets]) => {
      if (cancelled) return;
      setProfiles(nextProfiles);
      setPresets(nextPresets);
      setFormatsReady(true);
      if (!seededSave.current && nextProfiles.length === 0 && nextPresets.length === 0) {
        seededSave.current = true;
        setSaveProfile(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [phase, formatsKey]);

  function mappingBody(override?: ImportPreviewBody): ImportPreviewBody {
    if (override !== undefined) return override;
    if (presetId !== null) return { presetId };
    if (form.profileId !== null) return { profileId: form.profileId };
    return {
      headerRow: form.headerRow,
      firstDataRow: form.firstDataRow,
      columnMap: form.columnMap,
      amountMode: form.amountMode,
      outflowSign: form.outflowSign,
      dateFormat: form.dateFormat,
      delimiter: form.delimiter,
      encoding: form.encoding,
    };
  }

  async function runPreview(body?: ImportPreviewBody): Promise<PreviewOutcome> {
    const mine = ++seq.current;
    setPending(true);
    setFieldErrors(new Set());
    try {
      const next = await importsApi.preview(accountId, upload.batchId, mappingBody(body));
      if (mine !== seq.current || !alive.current) return 'stay';
      setPreview(next);
      setOwnPreview(true);
      setIncludeEarlier(true);
      setAcceptDifference(false);
      setStaleDifference(null);
      setPreviewError(null);
      setFxUnavailable(false);
      setCoveredThrough((prev) => (prev.length === 0 ? coveredDefault(next) : prev));
      if (next.fit !== null && next.fit.status === 'POOR') return 'poor';
      return 'advance';
    } catch (err) {
      if (mine !== seq.current || !alive.current) return 'stay';
      if (isApiError(err) && err.code === ErrorCodes.IMPORT_MAPPING_INVALID) {
        const named = namedInvalidFields(err).filter(
          (field) => NAMED_FIELDS.has(field) || ADVANCED_FIELDS.has(field),
        );
        setFieldErrors(new Set(named));
        setPreviewError(named.length === 0 ? t('imports.mappingInvalid') : null);
        setPreview(null);
        return 'stay';
      }
      if (isApiError(err) && err.code === ErrorCodes.FX_RATE_UNAVAILABLE) {
        setFxUnavailable(true);
        setPreviewError(null);
        return 'advance';
      }
      if (isApiError(err) && err.code === ErrorCodes.IMPORT_BATCH_STATE) {
        try {
          const next = await importsApi.get(accountId, upload.batchId);
          if (mine !== seq.current || !alive.current) return 'stay';
          setBatchStatus(next.status);
          setPreviewError(null);
          onBatchStateRef.current(next);
        } catch {
          if (mine === seq.current && alive.current) setPreviewError(t('imports.errorBatchState'));
        }
        return 'stay';
      }
      if (isApiError(err) && err.code === ErrorCodes.IMPORT_PRESET_NOT_FOUND) {
        setFormatsKey((key) => key + 1);
        setNotice(errorCopy(t, err, 'imports.errorGeneric'));
        return 'stay';
      }
      setPreviewError(errorCopy(t, err, 'imports.errorGeneric'));
      return 'stay';
    } finally {
      if (mine === seq.current && alive.current) setPending(false);
    }
  }

  const previewOnOpen = useEffectEvent(() => {
    void runPreview();
  });

  useEffect(() => {
    if (!autoPreview || autoStarted.current) return;
    autoStarted.current = true;
    previewOnOpen();
  }, [autoPreview]);

  async function reloadBatchState() {
    try {
      const next = await importsApi.get(accountId, upload.batchId);
      setBatchStatus(next.status);
      setPreviewError(null);
      onBatchState(next);
    } catch {
      setPreviewError(t('imports.errorBatchState'));
    }
  }

  function patchForm(partial: Partial<MappingForm>) {
    setMappingEdited(true);
    setPresetId(null);
    setForm((prev) => ({ ...prev, ...partial, profileId: null }));
  }

  // First data row follows the header (header + 1) until the person sets it (TMMAPP-0136 item 4b).
  function applyHeaderRow(row: number) {
    patchForm({
      headerRow: row,
      ...(!firstDataSet && row + 1 <= sampleMax ? { firstDataRow: row + 1 } : {}),
    });
  }

  function applyFirstDataRow(row: number) {
    setFirstDataSet(true);
    patchForm({ firstDataRow: row });
  }

  async function handleNext() {
    const outcome = await runPreview();
    if (!alive.current) return;
    if (outcome === 'advance' || outcome === 'poor') onAdvance();
  }

  async function applyFormat(value: string) {
    if (value === UNSAVED) return;
    if (value === SAVE_NEW) {
      setSaveNew(true);
      setSaveProfile(true);
      return;
    }
    setSaveNew(false);
    setSaveProfile(false);
    if (value.startsWith('profile:')) {
      const profile = profiles.find((item) => item.id === Number(value.slice('profile:'.length)));
      if (profile === undefined) return;
      setPresetId(null);
      setForm(formFromProfile(profile));
      const outcome = await runPreview({ profileId: profile.id });
      if (alive.current && outcome === 'advance') onAdvance();
      return;
    }
    if (value.startsWith('preset:')) {
      const preset = presets.find((item) => item.id === Number(value.slice('preset:'.length)));
      if (preset === undefined) return;
      setPresetId(preset.id);
      setForm(formFromProposal(preset.mapping, preset.delimiter, preset.encoding, null));
      const outcome = await runPreview({ presetId: preset.id });
      if (alive.current && outcome === 'advance') onAdvance();
    }
  }

  async function handleSuggest() {
    setSuggestError(null);
    setSuggestRefusal(null);
    setNoPrice(false);
    try {
      const next = await importsApi.suggestMapping(accountId, upload.batchId);
      setProposal(next);
      setPresetId(null);
      setForm(formFromProposal(next, form.delimiter, form.encoding, null));
    } catch (err) {
      const refusal = aiRefusal(err);
      if (refusal?.reason === 'NO_PRICE') {
        setNoPrice(true);
        setSuggestBlocked(true);
      } else if (isAiWalletRefusal(refusal)) {
        setSuggestRefusal(refusal);
        setSuggestBlocked(true);
      } else if (refusal !== null) {
        setSuggestError(aiDisabledCopy(t, refusal));
        setSuggestBlocked(true);
      } else if (isApiError(err) && err.code === ErrorCodes.AI_BUDGET_EXCEEDED) {
        setSuggestError(t('imports.aiCapped'));
        setSuggestBlocked(true);
      } else if (
        isApiError(err) &&
        (err.code === ErrorCodes.AI_NO_SUGGESTION ||
          err.code === ErrorCodes.AI_PROVIDER_UNAVAILABLE)
      ) {
        // TMMAPI-0174: an unusable answer isn't retryable, so the button goes; a call that failed
        // is, so the button stays (TMMAPP-0138 item 2).
        setSuggestError(errorCopy(t, err, 'imports.errorGeneric', undefined, 'IMPORT_MAPPING'));
        if (err.code === ErrorCodes.AI_NO_SUGGESTION) setSuggestBlocked(true);
      } else {
        setSuggestError(t('imports.errorGeneric'));
      }
    }
  }

  const institutionLabel = formatInstitution(institution, accountName);
  const fileType = formatFileType(upload.detected.delimiter, sourceFilename);
  const defaultName = t('imports.formatNameDefault', {
    institution: institutionLabel,
    fileType,
  });
  const profileName =
    typedName ??
    (profiles.some((p) => sameName(p.name, defaultName))
      ? uniqueName(
          t,
          defaultName,
          profiles.map((p) => p.name),
        )
      : defaultName);

  async function handleCommit() {
    if (committing || pending || (preview !== null && preview.errors.length > 0)) return;
    const openingPart = opening.collect();
    if (openingPart === null) return;
    setCommitting(true);
    setNameError(null);
    try {
      const send = (part: typeof openingPart) =>
        importsApi.commit(
          accountId,
          upload.batchId,
          {
            ...(coverageShown && coveredThrough.length > 0 ? { coveredThrough } : {}),
            ...(coverageShown ? { uncoveredPlanned: uncovered } : {}),
            ...(earlierCount > 0 && !includeEarlier ? { includeBeforeOpening: false } : {}),
            ...(openingCheckShown && differenceAsked && acceptDifference
              ? { acceptOpeningDifference: true }
              : {}),
            ...(presetId !== null ? { presetId } : {}),
            ...(saveProfile && profileName.trim().length > 0
              ? { saveProfile: { name: profileName.trim(), scope: profileScope } }
              : {}),
            ...part,
          },
          generateRequestId(),
        );
      let result: Awaited<ReturnType<typeof send>>;
      try {
        result = await send(openingPart);
      } catch (err) {
        const handled = opening.handleError(err);
        if (handled === 'shown') return;
        if (handled !== 'retry') throw err;
        result = await send({});
      }
      setImportedCount(importableCount);
      onCommitted(upload.batchId, result.historical, preview?.summary.firstDate ?? null);
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.PROFILE_NAME_TAKEN) {
        setNameError(t('imports.errorNameTaken'));
        focusErrors();
      } else if (isApiError(err) && err.code === ErrorCodes.FX_RATE_UNAVAILABLE) {
        setFxUnavailable(true);
      } else if (isApiError(err) && err.code === ErrorCodes.IMPORT_BATCH_STATE) {
        await reloadBatchState();
      } else if (isApiError(err) && err.code === ErrorCodes.IMPORT_PRESET_NOT_FOUND) {
        setFormatsKey((key) => key + 1);
        setNotice(errorCopy(t, err, 'imports.errorGeneric'));
      } else if (openingDiffers(err) !== null) {
        // The preview went stale (TMMAPP-0162 item 6): the served difference, in the same block,
        // unticked. Never retried by itself.
        setStaleDifference(openingDiffers(err));
        setAcceptDifference(false);
      } else {
        setPreviewError(t('imports.errorGeneric'));
      }
    } finally {
      setCommitting(false);
    }
  }

  async function handleDiscard() {
    await discarding.run(
      async () => {
        await importsApi.discard(accountId, upload.batchId);
        onDiscarded();
      },
      (error) => errorCopy(t, error, 'accounts.errorGeneric'),
    );
  }

  const headers = upload.detected.headerCandidates;
  const dateCell = firstSampleDateCell(
    upload.detected.sampleRows,
    preambleRows,
    form.firstDataRow,
    form.columnMap.date,
  );
  const minCovered = preview?.plannedCovered.coveredThrough ?? '';
  // The headline is the served total (`newCount`, TMMAPI-0191), less the earlier rows when unticked.
  const earlierCount = preview?.earlier.count ?? 0;
  const importableCount =
    preview === null ? 0 : preview.newCount - (includeEarlier ? 0 : earlierCount);
  // Every new line is on or before the account's opening (TMMAPP-0139 item 8): said at the top.
  const allBeforeOpening = preview !== null && preview.newer.count === 0 && earlierCount > 0;
  // The starting-balance block: under the counts while earlier history is included.
  const openingCheckShown =
    preview !== null &&
    earlierCount > 0 &&
    includeEarlier &&
    (preview.openingCheck !== null || staleDifference !== null);
  const differenceAsked =
    openingCheckShown && needsDifferenceAccepted(preview?.openingCheck ?? null, staleDifference);
  // "Covered through" and the planned-events choice only when lines would import and tmm-api
  // served the date they would cover to (TMMAPI-0176 §H: absent, never 1970-01-01).
  const coverageShown =
    preview === null || (preview.wouldImport > 0 && preview.plannedCovered.coveredThrough !== null);
  const commitDisabled =
    pending ||
    committing ||
    (preview !== null && preview.errors.length > 0) ||
    (preview !== null && importableCount === 0) ||
    (differenceAsked && !acceptDifference) ||
    (!fxUnavailable && (preview === null || previewError !== null));
  const liveFit: ImportFit | null = ownPreview ? (preview?.fit ?? null) : upload.fit;
  const view: FitView =
    preview !== null && liveFit !== null ? fitView(liveFit, preview) : 'results';
  // A mapped column is never hidden (TMMAPP-0140 item 1): Advanced opens when a format, a preset or
  // AI maps Memo or External id, and when a fit reason or a field error names a field in it. Each new
  // signal opens it once; the person may close it again.
  const nextAdvancedSignal = [
    ...ADVANCED_COLUMNS.flatMap((field) =>
      form.columnMap[field] !== null ? [`map:${field}:${form.columnMap[field]}`] : [],
    ),
    ...(liveFit?.reasons ?? []).flatMap((reason) =>
      reason.code === 'MAPPED_COLUMN_MISSING' &&
      reason.detail !== null &&
      ADVANCED_FIELDS.has(reason.detail)
        ? [`fit:${reason.detail}`]
        : [],
    ),
    ...[...fieldErrors].filter((field) => ADVANCED_FIELDS.has(field)).map((f) => `error:${f}`),
  ].join('|');
  if (nextAdvancedSignal !== advancedSignal) {
    setAdvancedSignal(nextAdvancedSignal);
    if (nextAdvancedSignal !== '') setAdvanced(true);
  }
  const usingProfile =
    form.profileId !== null &&
    (proposal.source === 'PROFILE' || form.profileId !== upload.suggestedProfileId);
  const usingProfileName =
    !usingProfile || form.profileId === null
      ? null
      : (profiles.find((p) => p.id === form.profileId)?.name ??
        (form.profileId === upload.suggestedProfileId ? upload.suggestedProfileName : null));
  const hasFormats = profiles.length + presets.length > 0;
  const showSaveFields = formatsReady && saveProfile && (!hasFormats || saveNew);
  const showSuggest =
    phase === 'configuration' &&
    aiMapping &&
    proposal.source !== 'AI' &&
    aiEnabled &&
    !suggestBlocked;
  const presetName =
    presetId === null
      ? null
      : presetId === upload.suggestedPresetId
        ? upload.suggestedPresetName
        : (presets.find((item) => item.id === presetId)?.name ?? null);
  const profileHeading =
    presetId !== null || form.profileId === null
      ? null
      : form.profileId === upload.suggestedProfileId
        ? upload.suggestedProfileName
        : (profiles.find((item) => item.id === form.profileId)?.name ?? null);
  const formatTitle =
    presetName !== null && presetName.length > 0
      ? t('imports.fit.formatPreset', { name: presetName })
      : profileHeading !== null && profileHeading.length > 0
        ? t('imports.fit.formatProfile', { name: profileHeading })
        : null;
  const formatValue = saveNew
    ? SAVE_NEW
    : presetId !== null
      ? `preset:${presetId}`
      : form.profileId !== null
        ? `profile:${form.profileId}`
        : UNSAVED;
  const formatKnown =
    formatValue === UNSAVED ||
    formatValue === SAVE_NEW ||
    profiles.some((item) => formatValue === `profile:${item.id}`) ||
    presets.some((item) => formatValue === `preset:${item.id}`);

  useEffect(() => {
    if (phase !== 'confirm' || formatTitle === null) return;
    titleRef.current?.focus();
  }, [phase, formatTitle]);

  const rowError = (field: string) =>
    fieldErrors.has(field) ? t('imports.mappingInvalid') : undefined;

  const saveFields = (
    <Stack spacing={1} data-save-format="">
      {!hasFormats && (
        <FormControlLabel
          control={
            <Checkbox checked={saveProfile} onChange={(_e, checked) => setSaveProfile(checked)} />
          }
          label={t('imports.saveProfile')}
        />
      )}
      {showSaveFields && (
        <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
          <TextField
            size="small"
            label={t('imports.profileName')}
            value={profileName}
            error={nameError !== null}
            helperText={phase === 'configuration' ? (nameError ?? undefined) : undefined}
            onChange={(event) => {
              setTypedName(event.target.value);
              setNameError(null);
            }}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="import-scope">{t('imports.profileScope')}</InputLabel>
            <Select
              labelId="import-scope"
              label={t('imports.profileScope')}
              value={profileScope}
              onChange={(event) => setProfileScope(event.target.value as 'ACCOUNT' | 'OWNER')}
            >
              <MenuItem value="ACCOUNT">{t('imports.scopeAccount')}</MenuItem>
              <MenuItem value="OWNER">{t('imports.scopeOwner')}</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      )}
    </Stack>
  );

  const mappingForm = (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
        <RowNumberField
          label={t('imports.headerRow')}
          value={form.headerRow}
          min={1}
          max={sampleMax}
          serverError={rowError('headerRow')}
          onApply={applyHeaderRow}
        />
        <RowNumberField
          label={t('imports.firstDataRow')}
          value={form.firstDataRow}
          min={1}
          max={sampleMax}
          serverError={rowError('firstDataRow')}
          onApply={applyFirstDataRow}
        />
      </Stack>
      {MAIN_COLUMNS.filter((field) =>
        form.amountMode === 'SIGNED' ? field !== 'debit' && field !== 'credit' : field !== 'amount',
      ).map((field) => (
        <FieldSelect
          key={field}
          field={field}
          value={form.columnMap[field]}
          headers={headers}
          onChange={(next) => patchForm({ columnMap: { ...form.columnMap, [field]: next } })}
        />
      ))}
      <FormControl>
        <FormLabel>{t('imports.amountMode')}</FormLabel>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={form.amountMode}
          onChange={(_e, value: MappingForm['amountMode'] | null) => {
            if (value) patchForm({ amountMode: value });
          }}
        >
          <ToggleButton value="SIGNED">{t('imports.amountSigned')}</ToggleButton>
          <ToggleButton value="DEBIT_CREDIT">{t('imports.amountDebitCredit')}</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      {form.amountMode === 'SIGNED' && (
        <FormControl>
          <FormLabel>{t('imports.outflowSign')}</FormLabel>
          <RadioGroup
            row
            value={form.outflowSign}
            onChange={(event) =>
              patchForm({ outflowSign: event.target.value as MappingForm['outflowSign'] })
            }
          >
            <FormControlLabel
              value="NEGATIVE"
              control={<Radio />}
              label={t('imports.outflowNegative')}
            />
            <FormControlLabel
              value="POSITIVE"
              control={<Radio />}
              label={t('imports.outflowPositive')}
            />
          </RadioGroup>
        </FormControl>
      )}
      <FormControl size="small" fullWidth>
        <InputLabel id="import-date-format">{t('imports.dateFormat')}</InputLabel>
        <Select
          labelId="import-date-format"
          label={t('imports.dateFormat')}
          value={form.dateFormat}
          onChange={(event) => patchForm({ dateFormat: event.target.value })}
        >
          {DATE_FORMATS.map((fmt) => {
            const parsed = parseDateCell(dateCell, fmt);
            return (
              <MenuItem key={fmt} value={fmt}>
                {parsed !== null
                  ? t('imports.dateFormatOption', { format: fmt, sample: parsed })
                  : fmt}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('imports.sourceCurrency', { currency, name: accountName })}
      </Typography>
      <Button
        size="small"
        aria-expanded={advanced}
        aria-controls={advancedId}
        onClick={() => setAdvanced((v) => !v)}
      >
        {t('imports.advanced')}
      </Button>
      <Collapse in={advanced} id={advancedId} data-advanced="">
        <Stack spacing={2}>
          {ADVANCED_COLUMNS.map((field) => (
            <FieldSelect
              key={field}
              field={field}
              value={form.columnMap[field]}
              headers={headers}
              error={rowError(field)}
              onChange={(next) => patchForm({ columnMap: { ...form.columnMap, [field]: next } })}
            />
          ))}
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
            <TextField
              size="small"
              label={t('imports.delimiter')}
              value={form.delimiter}
              error={rowError('delimiter') !== undefined}
              helperText={rowError('delimiter')}
              onChange={(event) => patchForm({ delimiter: event.target.value })}
            />
            <TextField
              size="small"
              label={t('imports.encoding')}
              value={form.encoding}
              error={rowError('encoding') !== undefined}
              helperText={rowError('encoding')}
              onChange={(event) => patchForm({ encoding: event.target.value })}
            />
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );

  const showCommitProgress = committing || importedCount !== null;
  const commitTitle =
    importedCount === null
      ? t('imports.importingCount', { count: importableCount })
      : t('imports.imported');
  const commitAnnouncement =
    importedCount === null ? '' : t('imports.importedCount', { count: importedCount });
  // IMPORT at zero rows is disabled, and says why (TMMAPP-0139 item 8).
  const zeroReasonId =
    preview !== null && importableCount === 0
      ? allBeforeOpening
        ? beforeOpeningNoticeId
        : tilesId
      : undefined;
  const commitButton = (
    <SubmitButton
      loading={committing}
      loadingLabel={t('imports.importing')}
      disabled={!committing && (commitDisabled || importedCount !== null)}
      aria-describedby={zeroReasonId}
      onClick={() => void handleCommit()}
    >
      {t('imports.importCount', { count: importableCount })}
    </SubmitButton>
  );
  const nextButton = (
    <SubmitButton
      loading={pending}
      loadingLabel={t('imports.updatingPreview')}
      disabled={pending}
      onClick={() => void handleNext()}
    >
      {t('imports.next')}
    </SubmitButton>
  );
  const closeButton = (
    <Button variant="contained" onClick={onCloseHost}>
      {t('imports.fit.close')}
    </Button>
  );
  const summary = preview?.summary ?? null;
  const previewShrinks = preview !== null && preview.sample.length > PREVIEW_MIN_ROWS;
  const plannedCoveredText =
    preview !== null && preview.plannedCovered.coveredThrough !== null
      ? t('imports.plannedCovered', {
          count: preview.plannedCovered.count,
          date: formatCivilDate(preview.plannedCovered.coveredThrough, locale),
        })
      : null;
  // A GOOD fit's reasons, "good to know", on both phases (TMMAPI-0176 §H grades a file before the
  // opening GOOD). Confirm says the before-opening ones in its own notices, so not twice.
  const goodToKnow: ImportFit | null =
    liveFit !== null && liveFit.status === 'GOOD'
      ? phase === 'confirm'
        ? { ...liveFit, reasons: liveFit.reasons.filter((r) => !BEFORE_OPENING_CODES.has(r.code)) }
        : liveFit
      : null;
  const allBeforeDate =
    openingOn ??
    liveFit?.reasons.find((reason) => reason.code === 'ALL_BEFORE_OPENING')?.detail ??
    null;
  // "Include earlier history" (TMMAPP-0162 item 6), ticked by default; it replaced "Import them
  // anyway". Unticking it drops the earlier rows from the headline and hides the starting balance.
  const includeBeforeChoice = (
    <FormControlLabel
      control={
        <Checkbox
          checked={includeEarlier}
          disabled={busy}
          onChange={(_event, checked) => {
            setIncludeEarlier(checked);
            setAcceptDifference(false);
          }}
        />
      }
      label={t('imports.includeEarlier')}
    />
  );
  // A part's dates (served whenever its count isn't 0): a shared year once, none for the current one.
  const spanRange = (span: ImportPreview['earlier']) =>
    span.count > 0 && span.from !== null && span.to !== null
      ? formatCivilRangeNear(span.from, span.to, locale, today)
      : null;
  const earlierRange = preview === null ? null : spanRange(preview.earlier);
  const newerRange = preview === null ? null : spanRange(preview.newer);
  // "244 new: 221 earlier (…), 23 newer (…)" when both parts count; a part at 0 isn't named.
  const splitText =
    preview !== null && includeEarlier && earlierRange !== null && newerRange !== null
      ? t('imports.split.line', {
          count: importableCount,
          earlier: t('imports.split.earlier', { count: earlierCount, range: earlierRange }),
          newer: t('imports.split.newer', { count: preview.newer.count, range: newerRange }),
        })
      : null;
  const alerts = (
    <>
      {notice !== null && <Alert severity="warning">{notice}</Alert>}
      {phase === 'confirm' && nameError !== null && <Alert severity="error">{nameError}</Alert>}
      {liveFit !== null && liveFit.status === 'POOR' && (
        <FitReasonsBanner fit={liveFit} lead={t('imports.fit.mappingPoor')} severity="warning" />
      )}
      {goodToKnow !== null && goodToKnow.reasons.length > 0 && (
        <FitReasonsBanner
          fit={goodToKnow}
          lead={t('imports.fit.goodWithReasons')}
          severity="info"
        />
      )}
      {previewError !== null && <Alert severity="error">{previewError}</Alert>}
      {fxUnavailable && <Alert severity="warning">{t('imports.fxUnavailable')}</Alert>}
      {batchStatus !== null && batchStatus !== 'COMMITTED' && batchStatus !== 'FAILED' && (
        <Box role="status">
          <StatusChip domain="importBatch" status={batchStatus} />
        </Box>
      )}
    </>
  );

  return (
    <Stack
      ref={rootRef}
      spacing={phase === 'confirm' ? { xs: 2, md: 1.5 } : 2}
      // Confirm fills the import body at `md` and up, with tighter gaps, so the line preview can give
      // up height (TMMAPP-0140 item 5); ImportModal makes the body a column on this step.
      sx={phase === 'confirm' ? { flex: { md: '1 1 auto' }, minHeight: 0 } : undefined}
    >
      <UnsavedChangesDialog guard={guard} />
      {phase === 'configuration' && (
        <>
          {alerts}
          {pending && <ImportActivityBar label={t('imports.updatingPreview')} />}
          {!gate.loading && gate.catalog !== null && gate.catalog.reviewedAt !== null && (
            <CurrentSettingsLine
              catalog={gate.catalog}
              onCatalogChange={() => void gate.reload()}
            />
          )}
          <Box
            data-confirm-layout=""
            sx={{
              display: 'grid',
              gap: 3,
              alignItems: 'start',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 380px' },
              // Below `sm` the sample comes first (TMMAPP-0139 item 4); `sm` to `lg` the form.
              gridTemplateAreas: {
                xs: '"sample" "form"',
                sm: '"form" "sample"',
                lg: '"sample form"',
              },
            }}
          >
            <Box sx={{ gridArea: 'form', minWidth: 0 }} data-mapping-form="">
              {formatsReady && hasFormats && (
                <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                  <InputLabel id="import-format">{t('imports.formatLabel')}</InputLabel>
                  <Select
                    labelId="import-format"
                    label={t('imports.formatLabel')}
                    value={formatValue}
                    onChange={(event) => void applyFormat(event.target.value)}
                    MenuProps={{
                      slotProps: {
                        paper: {
                          sx: { bgcolor: tokens.surfaceRaised, backgroundImage: 'none' },
                        },
                      },
                    }}
                  >
                    {!formatKnown && (
                      <MenuItem value={formatValue} sx={visuallyHidden}>
                        {formatValue}
                      </MenuItem>
                    )}
                    {formatValue === UNSAVED && (
                      <MenuItem value={UNSAVED}>
                        {t(
                          proposal.source === 'AI'
                            ? 'imports.formatUnsavedAi'
                            : 'imports.formatUnsavedDetected',
                        )}
                      </MenuItem>
                    )}
                    {profiles.map((item) => (
                      <MenuItem key={`profile-${item.id}`} value={`profile:${item.id}`}>
                        {item.name}
                      </MenuItem>
                    ))}
                    {presets.map((item) => (
                      <MenuItem key={`preset-${item.id}`} value={`preset:${item.id}`}>
                        {item.name}
                      </MenuItem>
                    ))}
                    <MenuItem value={SAVE_NEW}>{t('imports.saveNewFormat')}</MenuItem>
                  </Select>
                </FormControl>
              )}
              {formatsReady && !hasFormats && saveFields}
              {showSaveFields && hasFormats && saveFields}
              {usingProfileName !== null ? (
                <Alert severity="info" sx={{ mb: 1 }} data-using-format="">
                  {t('imports.usingFormat', { name: usingProfileName })}
                </Alert>
              ) : (
                <ProposalBanner proposal={proposal} locale={locale} />
              )}
              {showSuggest && (
                <Button size="small" onClick={() => void handleSuggest()} sx={{ mb: 1 }}>
                  {t('imports.suggestMapping')}
                </Button>
              )}
              {noPrice && (
                <Typography variant="caption" sx={{ color: tokens.infoText, display: 'block' }}>
                  {t('aiWallet.prompt.noPrice')}
                </Typography>
              )}
              {suggestError !== null && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {suggestError}
                </Alert>
              )}
              {suggestRefusal !== null && (
                <Box sx={{ mt: 1 }}>
                  <AiWalletPrompt
                    refusal={suggestRefusal}
                    onTryAgain={() => void handleSuggest()}
                    onCloseHost={onCloseHost}
                    swap={topUpSwap}
                  />
                </Box>
              )}
              <Box sx={{ mt: 2 }}>{mappingForm}</Box>
            </Box>
            <Box sx={{ gridArea: 'sample', minWidth: 0 }}>
              <RawSample
                rows={upload.detected.sampleRows}
                preambleSample={upload.detected.preambleSample}
                preambleFrom={upload.detected.preambleFrom}
                preambleRows={preambleRows}
                columnMap={form.columnMap}
                headerRow={form.headerRow}
                firstDataRow={form.firstDataRow}
                onPickHeader={applyHeaderRow}
                onPickFirstData={applyFirstDataRow}
              />
            </Box>
          </Box>
          {actionsHost !== undefined &&
            actionsHost !== null &&
            createPortal(nextButton, actionsHost)}
        </>
      )}
      {phase === 'confirm' && (
        <>
          {alerts}
          {pending && preview === null && (
            <ImportActivityBar label={t('imports.updatingPreview')} />
          )}
          {formatTitle !== null && (
            <Typography ref={titleRef} variant="h3" component="h3" tabIndex={-1}>
              {formatTitle}
            </Typography>
          )}
          {preview !== null && view === 'results' && (
            <Box role="status" sx={visuallyHidden}>
              {t('imports.fit.statusGood', { count: importableCount })}
            </Box>
          )}
          {preview !== null && view === 'alreadyImported' && (
            <Typography role="status" variant="body1">
              {t('imports.fit.alreadyImported', { count: preview.duplicates })}
            </Typography>
          )}
          {preview !== null && allBeforeOpening && view !== 'alreadyImported' && (
            <Alert severity="info" data-all-before-opening="">
              <Typography variant="body2" id={beforeOpeningNoticeId}>
                {allBeforeDate !== null
                  ? t('imports.fit.reason.ALL_BEFORE_OPENING', {
                      count: earlierCount,
                      date: formatCivilDate(allBeforeDate, locale),
                    })
                  : t('imports.fit.reason.BEFORE_OPENING', { count: earlierCount })}
              </Typography>
              <Stack direction="row" sx={{ alignItems: 'center', columnGap: 2, flexWrap: 'wrap' }}>
                {includeBeforeChoice}
                {onChangeOpening !== undefined && (
                  <Link component="button" type="button" variant="body2" onClick={onChangeOpening}>
                    {t('imports.changeOpeningDate')}
                  </Link>
                )}
              </Stack>
            </Alert>
          )}
          {/* The tiles say the counts; there is no summary line under them (TMMAPP-0140 item 5). */}
          {preview !== null && view !== 'alreadyImported' && (
            <Stack
              id={tilesId}
              direction="row"
              data-stat-tiles=""
              sx={{ gap: { xs: 1.5, md: 1 }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}
            >
              <StatBox label={t('imports.fit.tileNew')} variant={statVariant}>
                {formatNumber(importableCount, locale)}
              </StatBox>
              <StatBox label={t('imports.fit.tileDuplicates')} variant={statVariant}>
                {formatNumber(preview.duplicates, locale)}
              </StatBox>
              <StatBox label={t('imports.fit.tileErrors')} variant={statVariant}>
                {formatNumber(preview.errors.length, locale)}
              </StatBox>
              {summary !== null && summary.firstDate !== null && summary.lastDate !== null && (
                <StatBox label={t('imports.fit.tileDates')} variant={statVariant}>
                  {t('imports.fit.dateRange', {
                    from: formatCivilDate(summary.firstDate, locale),
                    to: formatCivilDate(summary.lastDate, locale),
                  })}
                </StatBox>
              )}
              {summary !== null && (
                <StatBox label={t('imports.fit.tileIn')} variant={statVariant}>
                  <MoneyText minor={summary.inMinor} currency={currency} variant={statVariant} />
                </StatBox>
              )}
              {summary !== null && (
                <StatBox label={t('imports.fit.tileOut')} variant={statVariant}>
                  <MoneyText minor={summary.outMinor} currency={currency} variant={statVariant} />
                </StatBox>
              )}
            </Stack>
          )}
          {splitText !== null && (
            <Typography variant="body2" data-import-split="">
              {splitText}
            </Typography>
          )}
          {preview !== null &&
            earlierCount > 0 &&
            !allBeforeOpening &&
            view !== 'alreadyImported' && <Box data-include-earlier="">{includeBeforeChoice}</Box>}
          {preview !== null && openingCheckShown && view !== 'alreadyImported' && (
            <EarlierHistoryOpening
              check={preview.openingCheck}
              stale={staleDifference}
              currency={currency}
              today={today}
              accepted={acceptDifference}
              onAcceptedChange={setAcceptDifference}
              disabled={busy}
            />
          )}
          {preview !== null && preview.fx !== null && (
            <Typography variant="body2">
              {t('imports.fxPair', {
                base: preview.fx.base,
                quote: preview.fx.quote,
                rate: formatNumber(preview.fx.rate, locale, { maximumFractionDigits: 6 }),
                asOf: formatCivilDate(preview.fx.asOf, locale),
              })}
            </Typography>
          )}
          {preview !== null &&
            preview.errors.slice(0, 5).map((err, i) => (
              <Typography
                key={`e-${i}`}
                variant="body2"
                sx={{ color: (nextTheme) => resolveTokens(nextTheme).errorText }}
              >
                {t('imports.rowUnreadableLine', { row: err.row })}
              </Typography>
            ))}
          {/* Confirm's one lines table (TMMAPP-0140 item 2): a saved format no longer adds a second. */}
          {preview !== null && (
            <Box sx={previewShellSx(previewShrinks, theme.spacing(26))}>
              {pending && <ImportActivityBar label={t('imports.updatingPreview')} overlay />}
              <Box
                role="region"
                aria-label={t('imports.sampleParsed')}
                tabIndex={0}
                data-preview-window=""
                sx={{
                  ...previewWindowSx(previewShrinks, theme.spacing(26)),
                  '&:focus-visible': {
                    outline: `2px solid ${tokens.focusRing}`,
                    outlineOffset: '2px',
                  },
                }}
              >
                <Table
                  size="small"
                  aria-label={t('imports.sampleParsed')}
                  // At `md` and up the rows take the compact density's 6px block padding, so five of
                  // them fit beside the tiles and Covered through at 1280 × 800 (TMMAPP-0140 item 5).
                  sx={{ '& .MuiTableCell-root': { paddingBlock: { md: 0.75 } } }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('imports.colPostedOn')}</TableCell>
                      <TableCell>{t('imports.colPayee')}</TableCell>
                      <TableCell align="right">{t('imports.colAmount')}</TableCell>
                      <TableCell>{t('imports.colMemo')}</TableCell>
                      <TableCell align="right">{t('imports.colBalance')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.sample.map((row, i) => (
                      <TableRow key={`s-${i}`}>
                        <TableCell>
                          <TabularText>{formatCivilDate(row.postedOn, locale)}</TabularText>
                        </TableCell>
                        <TableCell>
                          <PayeeCell text={row.payee} />
                        </TableCell>
                        <TableCell align="right">
                          <MoneyText minor={row.amountMinor} currency={currency} />
                        </TableCell>
                        <TableCell>{row.memo ?? '—'}</TableCell>
                        <TableCell align="right">
                          <MoneyText minor={row.runningBalanceMinor} currency={currency} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
          {view !== 'alreadyImported' && (
            <>
              {/* Covered through beside the planned-events choice at `md` and up (item 5). */}
              {coverageShown && (
                <Box
                  data-coverage=""
                  sx={{
                    flexShrink: 0,
                    display: 'grid',
                    gap: 2,
                    alignItems: 'start',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                  }}
                >
                  <Stack spacing={0.5}>
                    <FormControl>
                      <FormLabel htmlFor="import-covered-through">
                        {t('imports.coveredThrough')}
                      </FormLabel>
                      <TextField
                        id="import-covered-through"
                        size="small"
                        type="date"
                        value={coveredThrough}
                        slotProps={{
                          htmlInput: {
                            min: minCovered,
                            ...(plannedCoveredText !== null
                              ? { 'aria-describedby': plannedCoveredId }
                              : {}),
                          },
                        }}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (minCovered.length > 0 && next < minCovered) return;
                          setCoveredThrough(next);
                        }}
                      />
                    </FormControl>
                    {plannedCoveredText !== null && (
                      <Typography
                        id={plannedCoveredId}
                        variant="body2"
                        sx={{ color: tokens.text2 }}
                      >
                        {plannedCoveredText}
                      </Typography>
                    )}
                  </Stack>
                  <FormControl>
                    <FormLabel id="import-uncovered-label" sx={{ typography: 'body2' }}>
                      {t('imports.fit.plannedChoice')}
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="import-uncovered-label"
                      value={uncovered}
                      onChange={(event) => setUncovered(event.target.value as 'WIPE' | 'KEEP')}
                    >
                      <FormControlLabel
                        value="WIPE"
                        control={<Radio />}
                        label={t('accounts.coverageWipe')}
                      />
                      <FormControlLabel
                        value="KEEP"
                        control={<Radio />}
                        label={t('accounts.coverageKeep')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>
              )}
              {opening.node}
            </>
          )}
          {showCommitProgress && (
            <ImportProgress
              title={commitTitle}
              announcement={commitAnnouncement}
              bar={importedCount === null ? 'indeterminate' : 'none'}
              steps={[
                {
                  label: t('imports.importingCount', { count: importedCount ?? importableCount }),
                  state: importedCount === null ? 'active' : 'done',
                },
                {
                  label: t('imports.imported'),
                  state: importedCount === null ? 'pending' : 'done',
                },
              ]}
            />
          )}
          {actionsHost !== undefined &&
            actionsHost !== null &&
            createPortal(view === 'alreadyImported' ? closeButton : commitButton, actionsHost)}
        </>
      )}
      {view !== 'alreadyImported' && (
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          {actionsHost === undefined && phase === 'confirm' && commitButton}
          {actionsHost === undefined && phase === 'configuration' && nextButton}
          <Button
            color="error"
            disabled={committing || importedCount !== null}
            onClick={() => discarding.ask(true)}
          >
            {t('imports.discard')}
          </Button>
        </Stack>
      )}
      <DiscardPanel
        open={discarding.target !== null}
        panelHost={panelHost}
        onConfirming={onConfirming}
        title={t('imports.discardTitleAccount', { account: accountName })}
        consequence={t('imports.discardConsequence')}
        confirmLabel={t('imports.discardConfirm')}
        destructive
        busy={discarding.busy}
        error={discarding.error}
        onConfirm={() => void handleDiscard()}
        onCancel={discarding.cancel}
      />
    </Stack>
  );
}

function ProposalBanner({ proposal, locale }: { proposal: ImportProposedMapping; locale: string }) {
  const { t } = useTranslation();
  if (proposal.source === 'NONE') {
    return (
      <Alert severity="info" sx={{ mb: 1 }}>
        {t('imports.proposalNone')}
      </Alert>
    );
  }
  const percent = confidenceLabel(proposal.confidence, locale);
  return (
    <Alert severity="info" sx={{ mb: 1 }}>
      {proposal.source === 'AI' ? (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <AiPill confidence={proposal.confidence} />
          <Typography>{t('imports.proposedMapping')}</Typography>
        </Stack>
      ) : (
        <Typography>
          {t(`imports.proposalSource.${proposal.source}`, { confidence: percent })}
        </Typography>
      )}
      {proposal.notes.length > 0 && (
        <Box component="ul" sx={{ marginBlock: 0.5, paddingInlineStart: 2 }}>
          {proposal.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </Box>
      )}
    </Alert>
  );
}

function FieldSelect({
  field,
  value,
  headers,
  error,
  onChange,
}: {
  field: CanonicalField;
  value: number | null;
  headers: string[];
  /** A field error the preview named (an Advanced picker's, TMMAPP-0140 item 1). */
  error?: string | undefined;
  onChange: (value: number | null) => void;
}) {
  const { t } = useTranslation();
  const optional = OPTIONAL_FIELDS.has(field);
  return (
    <FormControl size="small" fullWidth error={error !== undefined}>
      <InputLabel id={`import-field-${field}`}>{t(`imports.field.${field}`)}</InputLabel>
      <Select
        labelId={`import-field-${field}`}
        label={t(`imports.field.${field}`)}
        value={value === null ? NONE : String(value)}
        onChange={(event) => {
          const next = event.target.value;
          onChange(next === NONE ? null : Number(next));
        }}
      >
        {optional && <MenuItem value={NONE}>{t('imports.fieldNone')}</MenuItem>}
        {headers.map((label, index) => (
          <MenuItem key={`${field}-${index}`} value={String(index)}>
            {label.length > 0 ? `${label} (${index})` : String(index)}
          </MenuItem>
        ))}
      </Select>
      {error !== undefined && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
}
